/**
 * Whether markets / locales are enabled. Affects static route generation
 * at build time (must be synchronous — runs at module load).
 *
 * Source of truth: Vercel environment variables, set on Production +
 * Preview in the `ahead-healths-projects` Vercel org. Migrated off
 * PostHog in #910.
 *
 * Each country/locale that can be gated uses four flags:
 *   - *_ENABLED   → routes are SSG'd. May still be gated by proxy.ts.
 *   - *_PUBLIC    → publicly advertised + reachable: in sitemap + hreflang +
 *                   locale picker, indexable, and the proxy preview gate is a
 *                   no-op. True at `waitlist`, `checkup`, and `launched`.
 *   - *_LAUNCHED  → blood check-up booking is live (waitlist mode off). True
 *                   at `checkup-preview`, `checkup`, and `launched`.
 *   - *_EXPANSION → MRI + membership booking is live too. `launched` only;
 *                   at `checkup` / `checkup-preview` these show a "coming
 *                   soon" pill (see `isComingSoon` / `isBookingOpen`).
 *
 * The `waitlist` status is PUBLIC but not LAUNCHED: "launched as a waitlist
 * site" — discoverable and indexable, but booking CTAs open the waitlist
 * modal (see `isWaitlistOnly`, which keys off !LAUNCHED). The `checkup`
 * status is LAUNCHED but not EXPANSION: "launched on blood only" — the
 * check-up books, MRI + membership stay coming-soon. `checkup-preview` is
 * the same booking behaviour as `checkup` but NOT public — token-gated like
 * `built`, so the blood-only launch can be rehearsed behind the preview link
 * before going public.
 *
 * Use `countries` for anything that needs the routes to physically exist
 * (route generation, internal navigation). Use `publicCountries` for
 * anything user-/Googlebot-facing (sitemap, hreflang, public discovery).
 */
const NL_ENABLED = process.env.NL_ENABLED === "true"
const NL_PUBLIC = process.env.NL_PUBLIC === "true"
const NL_LAUNCHED = process.env.NL_LAUNCHED === "true"
const NL_EXPANSION = process.env.NL_EXPANSION === "true"
const FR_ENABLED = process.env.FR_ENABLED === "true"
const FR_PUBLIC = process.env.FR_PUBLIC === "true"
const DE_ENABLED = process.env.DE_ENABLED === "true"
const DE_PUBLIC = process.env.DE_PUBLIC === "true"
const DE_LAUNCHED = process.env.DE_LAUNCHED === "true"
const DE_EXPANSION = process.env.DE_EXPANSION === "true"
// English inside Germany (`/de/en`), gated like `/ch/fr` is inside CH:
// DE_EN_STATUS derives ENABLED (route exists) and PUBLIC (advertised).
const DE_EN_ENABLED = process.env.DE_EN_ENABLED === "true"
const DE_EN_PUBLIC = process.env.DE_EN_PUBLIC === "true"
const AT_ENABLED = process.env.AT_ENABLED === "true"
const AT_PUBLIC = process.env.AT_PUBLIC === "true"
const AT_LAUNCHED = process.env.AT_LAUNCHED === "true"
const AT_EXPANSION = process.env.AT_EXPANSION === "true"

/**
 * Symptoms feature gate (the `/symptoms` lens + related-symptoms surfaces).
 * Single switch: `SYMPTOMS_ENABLED=true` makes the lens live everywhere —
 * routes render, indexed, nav tab + related-symptoms rows + search all on.
 * Fail-closed: an unset env var reads as `false`, so the feature stays dark
 * until explicitly flipped on. Preview by deploying a build with it on (e.g.
 * preflight / a branch deploy) rather than a per-visitor token.
 */
const SYMPTOMS_ENABLED = process.env.SYMPTOMS_ENABLED === "true"

export function areSymptomsEnabled(): boolean {
  return SYMPTOMS_ENABLED
}

export type Country = "ch" | "nl" | "de" | "at"

export interface CountryConfig {
  code: Country
  name: string
  flag: "ch" | "nl" | "de" | "at"
  currency: "CHF" | "EUR"
  defaultLanguage: string
  languages: string[]
  /** E.164 phone shown in JSON-LD, footer, contact page. */
  phone: string
  /** BCP47 locale used for Intl.NumberFormat / Intl.DateTimeFormat. */
  intlLocale: string
}

const CH: CountryConfig = {
  code: "ch",
  name: "Switzerland",
  flag: "ch",
  currency: "CHF",
  defaultLanguage: "de",
  languages: ["de", "en", ...(FR_ENABLED ? ["fr"] : [])],
  phone: "+41-44-797-69-46",
  intlLocale: "de-CH",
}

const NL: CountryConfig = {
  code: "nl",
  name: "Netherlands",
  flag: "nl",
  currency: "EUR",
  defaultLanguage: "nl",
  languages: ["nl", "en"],
  phone: "+31 85 345 3434",
  intlLocale: "nl-NL",
}

const DE: CountryConfig = {
  code: "de",
  name: "Germany",
  flag: "de",
  currency: "EUR",
  defaultLanguage: "de",
  languages: ["de", ...(DE_EN_ENABLED ? ["en"] : [])],
  phone: "+49 800 3453434",
  intlLocale: "de-DE",
}

const AT: CountryConfig = {
  code: "at",
  name: "Austria",
  flag: "at",
  currency: "EUR",
  defaultLanguage: "de",
  languages: ["de"],
  // Placeholder until an Austrian number is provisioned.
  phone: "+43 1 0000000",
  intlLocale: "de-AT",
}

/** The support channels a market shows in the footer and contact surfaces. */
export interface MarketSupport {
  /** Display form of the support line, as a customer should read it. */
  phone: string
  /** Digits only, for a `wa.me/<digits>` link. */
  whatsappDigits: string
}

/**
 * Swiss support answers for Switzerland and Austria — the same choice
 * `MARKET_FEATURES` makes in ahead-booking-new, where AT spreads
 * `SWISS_SUPPORT` until it has its own line.
 */
const SWISS_SUPPORT: MarketSupport = {
  phone: "+41 44 797 69 46",
  whatsappDigits: "41798078664",
}

/**
 * Support contacts per market. Keyed by every `Country`, so a new market has
 * to state its channels rather than inheriting Switzerland's by accident.
 *
 * The Netherlands has its own line; WhatsApp stays on the Dutch mobile it
 * used before the line moved. Both must stay identical to `nl.supportPhone` /
 * `nl.supportWhatsApp` in
 * ahead-booking-new `src/config/market.ts`: a Dutch customer reads it here and
 * then again in the booking app, and two numbers for one market reads as a
 * broken handover. This is the display form; the E.164 form that JSON-LD and
 * the imprint publish is `CountryConfig.phone`.
 */
export const MARKET_SUPPORT: Record<Country, MarketSupport> = {
  ch: SWISS_SUPPORT,
  // Germany has its own toll-free line; a 0800 number takes no WhatsApp, so
  // WhatsApp stays on the Swiss support number.
  de: {
    phone: "+49 800 3453434",
    whatsappDigits: SWISS_SUPPORT.whatsappDigits,
  },
  at: SWISS_SUPPORT,
  nl: { phone: "+31 85 345 3434", whatsappDigits: "31612640438" },
}

export const countries: CountryConfig[] = [
  CH,
  ...(NL_ENABLED ? [NL] : []),
  ...(DE_ENABLED ? [DE] : []),
  ...(AT_ENABLED ? [AT] : []),
]
/**
 * Countries (and their language sets) advertised to the public — used by
 * sitemap, hreflang, locale pickers, and any other surface where Googlebot
 * or anonymous visitors might discover an unlaunched market or locale.
 * Strict subset of `countries`:
 *   - NL appears once `nl-status` is "waitlist" or "launched" (PUBLIC)
 *   - CH is always present, but its `languages` exclude `fr` until
 *     `fr-status` is "waitlist" or "launched" (PUBLIC). The route exists at
 *     "built" but is gated by proxy.ts and excluded from public surfaces.
 *   - DE/AT appear once their `*_STATUS` is "waitlist" or "launched" (PUBLIC)
 *   - DE's `languages` exclude `en` until `de-en-status` is PUBLIC, the same
 *     way CH excludes `fr`
 */
const PUBLIC_CH: CountryConfig = {
  ...CH,
  languages: ["de", "en", ...(FR_PUBLIC ? ["fr"] : [])],
}
const PUBLIC_DE: CountryConfig = {
  ...DE,
  languages: ["de", ...(DE_EN_PUBLIC ? ["en"] : [])],
}
export const publicCountries: CountryConfig[] = [
  PUBLIC_CH,
  ...(NL_ENABLED && NL_PUBLIC ? [NL] : []),
  ...(DE_ENABLED && DE_PUBLIC ? [PUBLIC_DE] : []),
  ...(AT_ENABLED && AT_PUBLIC ? [AT] : []),
]
// `de` is added only when `DE_ENABLED=true`. Otherwise the locale-shortcut
// path `/de` would match `isKnownCountryCode("de")` and fall through to
// `isBlockedMarketPath` → 404 instead of being rewritten to `/ch/de/`.
// `at` is also flag-conditional for symmetry with `de`, even though `at`
// isn't a locale code (unlike `de`) so it couldn't cause the same conflict.
export const knownCountryCodes: readonly Country[] = [
  "ch",
  "nl",
  ...(DE_ENABLED ? ["de" as const] : []),
  ...(AT_ENABLED ? ["at" as const] : []),
] as const

/** All unique language codes across enabled countries */
export const locales = Array.from(
  new Set(countries.flatMap((c) => c.languages))
)
export type Locale = string

export interface MessageFileMeta {
  /** URL `[locale]` segment this file renders for. */
  language: string
  /** Market the file is served to. */
  country: Country
  market: string
  /**
   * Payload CMS locale this pair reads content in. Not derivable from the
   * id: the CMS keeps bare language codes for the Swiss catalogs (`en`,
   * `de`, `fr`) and for Dutch (`nl`); only the mirror markets have BCP 47
   * CMS locales (`en-NL`, `de-DE`, `de-AT`, `en-DE`).
   */
  cmsLocale: string
  /** Exactly one file is the English source of truth. */
  source?: true
  /**
   * The language's primary catalog — the one glossary
   * tooling treats as the reference translation for that language (de, fr, nl
   * today). Flagged explicitly rather than inferred from `id === language`,
   * which only holds while ids happen to be bare language codes.
   */
  primary?: true
  /** `false` keeps a file out of the human review queue (review-ledger). */
  reviewed?: false
}

/**
 * THE registry of translation catalogs. Every list of message files in the
 * repo derives from this object (`messageFiles`, `messageFilesWhere`,
 * `getMessageFile`); `__tests__/message-file-registry.test.ts` fails any
 * script that spells a file name itself. Keys are the message-file ids that
 * `getMessageFile` returns and `messages/<id>.json` on disk — each the BCP 47
 * tag of its (language, country) pair, so the Swiss German catalog is
 * `de-CH` and can never be read as Germany's `de-DE`.
 */
export const MESSAGE_FILES = {
  "en-CH": {
    language: "en",
    country: "ch",
    market: "Switzerland",
    cmsLocale: "en",
    source: true,
  },
  "de-CH": {
    language: "de",
    country: "ch",
    market: "Switzerland",
    cmsLocale: "de",
    primary: true,
  },
  "fr-CH": {
    language: "fr",
    country: "ch",
    market: "Switzerland",
    cmsLocale: "fr",
    primary: true,
  },
  "nl-NL": {
    language: "nl",
    country: "nl",
    market: "Netherlands",
    cmsLocale: "nl",
    primary: true,
  },
  "en-NL": {
    language: "en",
    country: "nl",
    market: "Netherlands",
    cmsLocale: "en-NL",
  },
  "de-DE": {
    language: "de",
    country: "de",
    market: "Germany",
    cmsLocale: "de-DE",
  },
  "de-AT": {
    language: "de",
    country: "at",
    market: "Austria",
    cmsLocale: "de-AT",
    reviewed: false,
  },
  "en-DE": {
    language: "en",
    country: "de",
    market: "Germany",
    cmsLocale: "en-DE",
  },
} as const satisfies Record<string, MessageFileMeta>

export type MessageFile = keyof typeof MESSAGE_FILES

/** Every URL `[locale]` value the registry serves, as a literal union. */
export type LanguageCode = (typeof MESSAGE_FILES)[MessageFile]["language"]

/** Every Payload CMS locale id the registry maps to, as a literal union. */
export type CmsLocale = (typeof MESSAGE_FILES)[MessageFile]["cmsLocale"]

export const messageFiles: readonly MessageFile[] = Object.keys(
  MESSAGE_FILES
) as MessageFile[]

export function isMessageFile(id: string): id is MessageFile {
  return Object.prototype.hasOwnProperty.call(MESSAGE_FILES, id)
}

export function messageFilesWhere(
  pred: (meta: MessageFileMeta, id: MessageFile) => boolean
): MessageFile[] {
  return messageFiles.filter((id) => pred(MESSAGE_FILES[id], id))
}

const [sourceMessageFile] = messageFilesWhere((m) => m.source === true)
if (!sourceMessageFile) throw new Error("MESSAGE_FILES has no source file")
export const SOURCE_MESSAGE_FILE: MessageFile = sourceMessageFile

/** Every URL `[locale]` value the site could serve, flag-independent. */
export const languageCodes: readonly string[] = Array.from(
  new Set(messageFiles.map((id) => MESSAGE_FILES[id].language))
)

export const defaultCountry: Country = "ch"

/**
 * All country configs regardless of feature flags. Use this when formatting
 * should work for any known country even if its routes aren't yet enabled
 * (e.g. Intl-based number/date formatting in lib/format.ts).
 */
export const allCountryConfigs: readonly CountryConfig[] = [CH, NL, DE, AT]

/** Lookup helpers */
export function getCountryConfig(country: string): CountryConfig | undefined {
  return countries.find((c) => c.code === country)
}

/**
 * Like getCountryConfig but flag-independent. Returns the config for any
 * known country regardless of NL_ENABLED / DE_ENABLED etc.
 */
export function getCountryConfigStatic(
  country: string
): CountryConfig | undefined {
  return allCountryConfigs.find((c) => c.code === country)
}

export function isKnownCountryCode(country: string): country is Country {
  return (knownCountryCodes as readonly string[]).includes(country)
}

/**
 * Whether a market is in "waitlist-only" mode: routes exist (ENABLED) but
 * booking isn't live yet (not LAUNCHED). In this state the booking CTA opens
 * the waitlist modal, the top banner points at locations instead of
 * insurance, and country-specific contact channels (Swiss phone / WhatsApp)
 * are hidden.
 *
 * Independent of PUBLIC: waitlist mode covers BOTH the private `built` state
 * (token-gated preview) and the public `waitlist` state ("launched as a
 * waitlist site" — discoverable + indexable, booking still closed). Only
 * flipping the status to `launched` (LAUNCHED=true) opens booking and takes
 * the country out of waitlist mode.
 */
export function isWaitlistOnly(country: string): boolean {
  if (country === "nl") return NL_ENABLED && !NL_LAUNCHED
  if (country === "de") return DE_ENABLED && !DE_LAUNCHED
  if (country === "at") return AT_ENABLED && !AT_LAUNCHED
  return false
}

/**
 * The bookable product families a market opens in waves. `blood` is the
 * check-up that goes live first (gated by `*_LAUNCHED` / `isWaitlistOnly`);
 * `mri` (full-body MRI + its add-ons like DEXA) and `membership` are the
 * expansion products that follow once the clinic / sign-up flow is ready in
 * the market (gated by `*_EXPANSION`).
 */
export type ProductCategory = "blood" | "mri" | "membership"

/**
 * Whether a market has opened booking for the expansion products (MRI +
 * membership). True only at `launched`; false at `checkup` (blood live,
 * expansion still coming soon) and below. CH (and any non-status market)
 * is always fully launched.
 */
function isExpansionLaunched(country: string): boolean {
  if (country === "nl") return NL_EXPANSION
  if (country === "de") return DE_EXPANSION
  if (country === "at") return AT_EXPANSION
  return true
}

/**
 * Whether a product category's booking flow is open in this market.
 * `blood` keys off `!isWaitlistOnly` (the check-up launch); `mri` and
 * `membership` additionally require the market to have launched its
 * expansion products. Drives whether a `BookingCta` links to the booking
 * app or opens the waitlist modal.
 */
export function isBookingOpen(
  country: string,
  category: ProductCategory = "blood"
): boolean {
  if (isWaitlistOnly(country)) return false
  if (category === "blood") return true
  return isExpansionLaunched(country)
}

/**
 * Whether a product category should render the "coming soon" treatment — a
 * pill on its nav item / card and a CTA that opens the waitlist modal. True
 * for `mri` / `membership` at `checkup` status (check-up bookable, expansion
 * not yet); false once `launched`.
 *
 * Waitlist markets (`built` / `waitlist`) single out `mri` only (2026-07-29
 * decision): the blood check-up is the launch product and shows its price
 * without a pill, MRI is explicitly signposted as coming later, and
 * membership isn't offered at all pre-launch — its surfaces hide entirely
 * rather than promise it (see the pricing/nav membership gates).
 */
export function isComingSoon(
  country: string,
  category: ProductCategory
): boolean {
  if (category === "blood") return false
  if (isWaitlistOnly(country)) return category === "mri"
  return !isExpansionLaunched(country)
}

/**
 * Whether the waitlist modal can be triggered anywhere in this market — used
 * by the always-mounted `<WaitlistModal>` to decide whether to render at all.
 * True when the whole market is waitlist-only (`built`/`waitlist`) OR when an
 * expansion product is coming-soon (`checkup`, where the blood check-up books
 * but MRI / membership CTAs still open the modal).
 */
export function isWaitlistReachable(country: string): boolean {
  return (
    isWaitlistOnly(country) ||
    isComingSoon(country, "mri") ||
    isComingSoon(country, "membership")
  )
}

export function isEnabledCountry(country: string): boolean {
  return getCountryConfig(country) !== undefined
}

export function isValidCountryLanguage(
  country: string,
  language: string
): boolean {
  const config = getCountryConfig(country)
  return config ? config.languages.includes(language) : false
}

/**
 * Every language a known country could ever serve, independent of
 * NL_ENABLED / FR_ENABLED / DE_ENABLED. Kept separate from the gated
 * `CountryConfig.languages` so disabled markets can still be validated.
 *
 * Derived from MESSAGE_FILES (the languages are grouped by `country`, in
 * registry order) so it cannot drift from the catalogs: the layout validates
 * with `isKnownCountryLanguage` and then calls `getMessageFile`, which 404s
 * on a pair with no catalog — a pair listed here but absent from the registry
 * would be a served page that 404s. Country keys stay spelled out so the `Country` union is
 * checked exhaustively.
 */
const STATIC_COUNTRY_LANGUAGES: Record<Country, readonly string[]> = (() => {
  const byCountry: Record<Country, string[]> = {
    ch: [],
    nl: [],
    de: [],
    at: [],
  }
  for (const id of messageFiles) {
    const { country, language } = MESSAGE_FILES[id]
    if (!byCountry[country].includes(language))
      byCountry[country].push(language)
  }
  return byCountry
})()

/**
 * Whether a language is one the given country could ever serve, read from
 * the registry instead of the flag-narrowed `CountryConfig.languages` — so
 * CH + `fr` validates even while `FR_ENABLED` is off. The COUNTRY half is
 * still flag-gated: `isKnownCountryCode` lists `de` / `at` only when
 * `DE_ENABLED` / `AT_ENABLED` are set, so a switched-off market returns
 * false here. For surfaces that must render gated markets — chiefly the
 * Payload live-preview iframe, whose real access boundary is draft mode +
 * `PAYLOAD_PREVIEW_SECRET`, not the flag. Public access to disabled markets
 * is gated upstream in `proxy.ts` (`isBlockedMarketPath`).
 */
export function isKnownCountryLanguage(
  country: string,
  language: string
): boolean {
  if (!isKnownCountryCode(country)) return false
  return STATIC_COUNTRY_LANGUAGES[country].includes(language)
}

export function getCountryLocalePairs(): { country: string; locale: string }[] {
  return countries.flatMap((c) =>
    c.languages.map((lang) => ({ country: c.code, locale: lang }))
  )
}

/**
 * The message file for a country + URL-locale pair. Every served pair has
 * exactly one file (see MESSAGE_FILES); any other pair comes from a junk URL
 * (bots probe `/pricing-OR-https%3A/aheadhealth.com/…`), so it 404s here
 * rather than relying on every page and metadata function to validate first.
 */
/**
 * The digest `next/navigation`'s `notFound()` throws
 * (`next/dist/client/components/not-found.js`, via
 * `HTTP_ERROR_FALLBACK_ERROR_CODE` in `http-access-fallback.js`). The router
 * honors any error carrying it (`isHTTPAccessFallbackError` reads only
 * `error.digest`), so an unknown pair 404s here without importing
 * `next/navigation` — that subpath does not resolve under plain-Node runners,
 * and this module is loaded by Playwright specs, tsx scripts and the edge
 * proxy, where a top-level `next/*` import fails the whole suite at load.
 */
const NOT_FOUND_DIGEST = "NEXT_HTTP_ERROR_FALLBACK;404"

function throwNotFound(): never {
  throw Object.assign(new Error(NOT_FOUND_DIGEST), {
    digest: NOT_FOUND_DIGEST,
  })
}

export function getMessageFile(locale: string, country: string): MessageFile {
  const id = `${locale}-${country.toUpperCase()}`
  if (!isMessageFile(id)) throwNotFound()
  return id
}

/**
 * Get the full BCP 47 locale code for a country+language pair.
 * Used for <html lang>, Intl formatting, and toLocaleDateString.
 * e.g. ("ch", "de") → "de-CH", ("nl", "en") → "en-NL"
 */
export function getBcp47Locale(locale: string, country: string): string {
  return `${locale}-${country.toUpperCase()}`
}

/** Compact labels for the picker trigger (flag + code). */
export const localeLabels: Record<string, string> = {
  en: "EN",
  de: "DE",
  fr: "FR",
  nl: "NL",
}

/**
 * Language names in their own language, for the picker's language options.
 * Flags mark the *region*; languages always show their native name so a
 * visitor recognises their own language regardless of the current UI locale
 * (localization best practice — flags can't represent a language).
 */
export const nativeLocaleLabels: Record<string, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  nl: "Nederlands",
}
